/**
 * `(setup)/index` -- a UI-less dispatcher.
 *
 * Entering the group lands here, and here decides where the user actually
 * belongs: the username claim for a social signup that still has a placeholder
 * handle, otherwise the step the server recorded. It reuses `resolveSetupRoute`
 * rather than re-deriving the order, so resume and the layout's enforcement can
 * never disagree.
 *
 * Asking the resolver for `username` is the whole trick: it answers `null` when
 * the claim is genuinely the right place to be, and the current step's route
 * otherwise.
 */

import { Redirect } from 'expo-router';
import * as React from 'react';

import { resolveSetupRoute } from '@/features/setup/gate/resolve-setup-route';
import { useSetupContext } from '@/features/setup/setup-provider';
import { useAuth } from '@/hooks/use-auth';

import { SETUP_ROUTE_HREFS } from './setup-hrefs';

export function SetupEntryDispatcher() {
  const { user } = useAuth();
  const { currentStep } = useSetupContext();

  const usernamePending = user?.usernamePending === true;

  // Dispatching on a placeholder step would send a resuming user back to the
  // start; waiting a tick costs nothing because nothing is painted yet.
  if (!usernamePending && currentStep === null) return null;

  const target =
    resolveSetupRoute({ usernamePending, currentStep, requestedRoute: 'username' }) ?? 'username';

  return <Redirect href={SETUP_ROUTE_HREFS[target]} />;
}
